import { Checkbox, Classes, Dialog, InputGroup } from "@blueprintjs/core"
import { observer } from "mobx-react"
import { formatMoney } from "modules/common/components/money/Money"
import { MoneyInput } from "modules/common/components/money/MoneyInput"
import { PeriodSlider } from "modules/common/components/period/PeriodSlider"
import { PlanrButton } from "modules/common/components/planr/button/Button"
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon"
import { LocalLoader } from "modules/common/components/loader/Loader"
import { RemoveConfirmation } from "modules/common/components/form/RemoveConfirmation"
import { capitalize } from "lodash"
import React, { useEffect, useState } from "react"
import { CompensationHistoryType, CompensationSourceLabels } from "../models/compensation-history"
import styles from "./CompensationDialog.module.scss"

interface CompensationDialogProps {
  isOpen: boolean
  onClose: () => void
  store: CompensationHistoryType
  employerId: string
  canEdit: boolean
}

const today = () => {
  const now = new Date()
  return { year: now.getFullYear(), month: now.getMonth() + 1 }
}

/**
 * История условий оплаты сотрудника: с какого месяца какие оклад, премия, вычет и доплаты.
 * Изменение с месяца сразу применяется к штатному расписанию (до следующего изменения).
 */
export const CompensationDialog = observer(({ isOpen, onClose, store, employerId, canEdit }: CompensationDialogProps) => {
  const [period, setPeriod] = useState(today())
  const [salary, setSalary] = useState<number | null>(null)
  const [bonus, setBonus] = useState<number | null>(null)
  const [onlyThisMonth, setOnlyThisMonth] = useState(false)
  const [comment, setComment] = useState("")
  const [editing, setEditing] = useState(false)

  useEffect(() => {
    if (isOpen) {
      store.load(employerId)
      setEditing(false)
    }
  }, [isOpen, employerId, store])

  // форма начинается с условий, действующих в выбранном месяце
  const startEdit = (year: number, month: number) => {
    const current = store.resolve(year, month)
    setPeriod({ year, month })
    setSalary(current ? current.salary : 0)
    setBonus(current ? current.bonus : 0)
    setOnlyThisMonth(false)
    setComment("")
    setEditing(true)
  }

  const changePeriod = (year: number, month: number) => {
    const current = store.resolve(year, month)
    setPeriod({ year, month })
    setSalary(current ? current.salary : 0)
    setBonus(current ? current.bonus : 0)
  }

  const save = async () => {
    const ok = await store.save({
      year: period.year,
      month: period.month,
      salary: salary ?? 0,
      bonus: bonus ?? 0,
      onlyThisMonth,
      comment,
    })
    if (ok) {
      setEditing(false)
    }
  }

  return (
    <Dialog
      icon="info-sign"
      title="Условия оплаты"
      canEscapeKeyClose={true}
      canOutsideClickClose={false}
      isOpen={isOpen}
      onClose={onClose}
      style={{ width: "1180px" }}
      backdropClassName="standard"
      className={`figma-dialog ${styles.dialog}`}
    >
      <div className={`${Classes.DIALOG_BODY} ${styles.body}`}>
        <LocalLoader active={store.loading} />

        {canEdit && !editing && (
          <div className={styles.toolbar}>
            <PlanrButton
              type="lightblueish"
              size="small"
              onClick={() => startEdit(today().year, today().month)}
            >
              Изменить с месяца
            </PlanrButton>
            <span className={styles.note}>
              Условия действуют с указанного месяца до следующего изменения и сразу применяются к штатному
              расписанию. Разовые суммы месяца (надбавка, корректировка премии) не меняются.
            </span>
          </div>
        )}

        {canEdit && editing && (
          <div className={styles.form}>
            <div className={styles.field}>
              <label>С месяца</label>
              <PeriodSlider year={period.year} month={period.month} onChange={changePeriod} />
            </div>
            <div className={styles.field}>
              <label>Оклад</label>
              <MoneyInput
                className="planr-default-input"
                value={salary ?? ""}
                onMoneyChange={(money) => setSalary(money.floatValue ?? 0)}
              />
            </div>
            <div className={styles.field}>
              <label>Премия</label>
              <MoneyInput
                className="planr-default-input"
                value={bonus ?? ""}
                onMoneyChange={(money) => setBonus(money.floatValue ?? 0)}
              />
            </div>
            <div className={styles.field}>
              <label>Комментарий</label>
              <InputGroup
                className="planr-default-input"
                autoComplete="off"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
            </div>
            <div className={styles.field}>
              <Checkbox
                checked={onlyThisMonth}
                label="Только этот месяц (исправление)"
                onChange={(e) => setOnlyThisMonth((e.target as HTMLInputElement).checked)}
              />
            </div>
            <div className={styles.buttons}>
              <PlanrButton type="greenish" size="small" onClick={save}>
                Сохранить
              </PlanrButton>
              <PlanrButton type="graybtn" size="small" onClick={() => setEditing(false)}>
                Отмена
              </PlanrButton>
            </div>
          </div>
        )}

        <div className={styles.table}>
          <div className={`${styles.row} ${styles.header}`}>
            <div className={styles.month}>С месяца</div>
            <div className={styles.money}>Оклад</div>
            <div className={styles.money}>Премия</div>
            <div className={styles.money}>Вычет</div>
            <div className={styles.money}>Доплаты</div>
            <div className={styles.source}>Основание</div>
            <div className={styles.author}>Кто внёс</div>
            <div className={styles.actions} />
          </div>
          {store.rows.length === 0 && !store.loading && (
            <div className={styles.row}>Условия оплаты ещё не заданы</div>
          )}
          {store.rows.map((row) => (
            <div key={row.since} className={styles.row}>
              <div className={styles.month}>{capitalize(row.month.format("MMMM YYYY"))}</div>
              <div className={styles.money}>{formatMoney(row.salary)}</div>
              <div className={styles.money}>{formatMoney(row.bonus)}</div>
              <div className={styles.money}>{row.decrease ? formatMoney(row.decrease) : "-"}</div>
              <div
                className={styles.money}
                title={row.additionalSums.map((s) => `${s.name}: ${formatMoney(s.sum)}`).join("\n")}
              >
                {row.additionalTotal ? formatMoney(row.additionalTotal) : "-"}
              </div>
              <div className={styles.source}>
                {CompensationSourceLabels[row.source ?? ""] ?? row.source ?? "-"}
                {row.comment ? <div className={styles.comment}>{row.comment}</div> : null}
              </div>
              <div className={styles.author}>{row.author || "-"}</div>
              <div className={styles.actions}>
                {canEdit && (
                  <>
                    <GeneralIcon
                      type="general-edit"
                      title="Изменить с этого месяца"
                      className={styles.editIcon}
                      onClick={() => startEdit(row.month.year(), row.month.month() + 1)}
                    />
                    <RemoveConfirmation
                      onConfirmed={() => store.remove(row.since)}
                      what={() => `запись с ${row.month.format("MMMM YYYY")} — снова будут действовать прежние условия`}
                    >
                      {({ confirmRemoving }) => (
                        <GeneralIcon
                          type="general-trash"
                          title="Удалить запись"
                          className={styles.removeIcon}
                          onClick={confirmRemoving}
                        />
                      )}
                    </RemoveConfirmation>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Dialog>
  )
})
